import { Bell, MessageSquare, Building2, Settings, Shield, ShoppingCart, Package } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUserRole, UserRole } from "@/hooks/useUserRole";
import { CompactSearchBar } from "@/components/search/CompactSearchBar";
import { supplierNavConfig } from "@/config/navigation/supplierNav";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { NavProfileMenu } from "@/components/navigation/NavProfileMenu";
import { getDisplayName, getFallbackInitial, getDisplayImage } from "@/lib/utils";
import { useUserProfile } from "@/hooks/useUserProfile";
import { logger } from "@/lib/logger";

export function SupplierTopNav() {
  const navigate = useNavigate();
  const { allRoles, role: currentRole, switchRole } = useUserRole();
  const navContext = useNavigationContext();
  const { profile } = useUserProfile();
  const primaryCTA = supplierNavConfig.primaryCTA;
  const complianceLinks = useMemo(
    () =>
      supplierNavConfig.sections.find((section) => section.id === "compliance")
        ?.items ?? [],
    []
  );

  // Fetch unread notifications
  const { data: notificationCount } = useQuery({
    queryKey: ['supplier-notifications'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from('notification_history')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) {
        logger.warn('Error fetching notifications count', { error });
        return 0;
      }

      return count || 0;
    },
    staleTime: 30 * 1000, // Cache for 30 seconds - consistent across all portals
    refetchInterval: 60 * 1000, // Refetch every minute - consistent across all portals
  });

    const messageCount = navContext.unreadMessages || 0;

  // Profile data comes from centralized useUserProfile hook

  const handleRoleSwitch = (newRole: UserRole) => {
    // Switch role first
    switchRole(newRole);
    toast.success(`Switched to ${newRole} portal`);
    
    // Use a longer delay and force navigation to ensure ProtectedRoute sees the updated state
    // The delay allows React to update all state and re-render components
    setTimeout(() => {
      // Force navigation with replace to prevent back button issues
      if (newRole === 'admin') {
        window.location.href = '/admin/dashboard';
      } else if (newRole === 'supplier') {
        window.location.href = '/supplier/dashboard';
      } else if (newRole === 'buyer') {
        window.location.href = '/buyer/dashboard';
      }
    }, 100);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
    navigate('/auth');
  };

  const profileActions = [
    {
      label: "Company Profile",
      icon: Building2,
      onSelect: () => navigate("/supplier/profile"),
    },
    {
      label: "Settings",
      icon: Settings,
      onSelect: () => navigate("/supplier/settings"),
    },
  ];

  // Helper function to get portal icon
  const getRoleIcon = (role: UserRole) => {
    if (role === "admin") return Shield;
    if (role === "supplier") return Package;
    return ShoppingCart;
  };

  const roleActions =
    allRoles.length > 1
      ? allRoles
          .filter((role) => role !== currentRole)
          .map((role) => ({
            label:
              role === "admin"
                ? "Admin Portal"
                : role === "supplier"
                ? "Supplier Portal"
                : "Buyer Portal",
            icon: getRoleIcon(role),
            onSelect: () => handleRoleSwitch(role),
          }))
      : undefined;

  const complianceSections =
    complianceLinks.length > 0
      ? [
          {
            label: "Compliance & Support",
            items: complianceLinks.map((item) => ({
              label: item.label,
              icon: item.icon,
              onSelect: () => navigate(item.href),
            })),
          },
        ]
      : undefined;

  return (
    <header className="flex h-16 min-w-0 items-center gap-2 overflow-hidden border-b border-border bg-white px-6 sm:gap-4">
        <div className="hidden shrink-0 items-center gap-2 sm:gap-4 md:flex">
          <Button size="sm" onClick={() => navigate(primaryCTA.href)} className="min-w-0 max-w-full overflow-hidden whitespace-nowrap">
            <primaryCTA.icon className="mr-2 size-4 shrink-0" />
            <span className="truncate">{primaryCTA.label}</span>
          </Button>
        </div>

        <div className="mx-8 min-w-0 max-w-xl flex-1">
        <CompactSearchBar
          placeholder="Search products, suppliers, RFQs..."
        />
      </div>

        <div className="ml-auto flex shrink-0 items-center gap-6 overflow-visible">
        {/* Notifications */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="group relative overflow-visible text-muted-foreground transition-colors hover:text-muted-foreground"
                onClick={() => navigate('/supplier/notifications')}
              >
                <Bell className="size-5" />
                {notificationCount > 0 && (
                  <Badge variant="default" className="absolute -right-1 -top-1 z-10 flex size-5 min-w-5 items-center justify-center p-0 text-xs font-semibold">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <div className="font-semibold">{notificationCount || 0} unread notifications</div>
                <div className="text-xs text-muted-foreground">RFQs, order updates, and messages</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Messages */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="group relative overflow-visible text-muted-foreground transition-colors hover:text-muted-foreground"
                onClick={() => navigate('/supplier/messages')}
              >
                <MessageSquare className="size-5" />
                {messageCount > 0 && (
                  <Badge variant="default" className="absolute -right-1 -top-1 z-10 flex size-5 min-w-5 items-center justify-center p-0 text-xs font-semibold">
                    {messageCount > 99 ? '99+' : messageCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <div className="font-semibold">{messageCount || 0} unread messages</div>
                <div className="text-xs text-muted-foreground">Conversations with buyers</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <NavProfileMenu
          name={getDisplayName(profile, 'supplier', 'navigation')}
          email={profile?.email || undefined}
          avatarUrl={getDisplayImage(profile, 'supplier', 'navigation') || undefined}
          fallbackInitial={getFallbackInitial(profile, 'supplier', 'navigation')}
          primaryActions={profileActions}
          sections={complianceSections}
          roleActions={roleActions}
          onSignOut={handleSignOut}
        />
      </div>
    </header>
  );
}
